const CONSENT_KEY = "cookieConsent";
const CONSENT_EVENT = "cookieConsentChanged";

// Keep tracking IDs in code (as requested).
const GA_MEASUREMENT_IDS = ["G-ED8LKQF38Q", "G-8VR1CXPZ2G"];
const META_PIXEL_ID = "1676658073339805";

let analyticsInitialized = false;

const getCurrency = () => process.env.REACT_APP_CURRENCY || "EUR";
const hasAcceptedConsent = () => getCookieConsent() === "accepted";

const createScript = (id, src) => {
    if (document.getElementById(id)) return;
    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
};

const initGoogleAnalytics = () => {
    if (!GA_MEASUREMENT_IDS.length) return;
    createScript("ga-script", `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_IDS[0]}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    GA_MEASUREMENT_IDS.forEach((id) => {
        window.gtag("config", id, { anonymize_ip: true });
        window[`ga-disable-${id}`] = false;
    });
};

const initMetaPixel = () => {
    if (!META_PIXEL_ID) return;
    if (!window.fbq) {
        (function (f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function () {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = true;
            n.version = "2.0";
            n.queue = [];
            t = b.createElement(e);
            t.async = true;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    }
    window.fbq("consent", "grant");
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
};

const removeKnownAnalyticsCookies = () => {
    const cookieNames = ["_ga", "_gid", "_gat", "_fbp", "_fbc"];
    cookieNames.forEach((name) => {
        document.cookie = `${name}=; Max-Age=0; path=/`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${window.location.hostname}`;
    });
};

export const getCookieConsent = () => localStorage.getItem(CONSENT_KEY);

/** Clears localStorage but keeps the user's cookie consent choice. */
export const clearLocalStoragePreservingConsent = () => {
    const consent = localStorage.getItem(CONSENT_KEY);
    localStorage.clear();
    if (consent) {
        localStorage.setItem(CONSENT_KEY, consent);
    }
};

export const dispatchConsentChange = (value) => {
    localStorage.setItem(CONSENT_KEY, value);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { consent: value } }));
};

export const applyConsent = (consent) => {
    if (consent === "accepted") {
        if (!analyticsInitialized) {
            initGoogleAnalytics();
            initMetaPixel();
            analyticsInitialized = true;
        } else {
            if (window.fbq) window.fbq("consent", "grant");
            GA_MEASUREMENT_IDS.forEach((id) => {
                window[`ga-disable-${id}`] = false;
            });
        }
        return;
    }

    // For "declined" (or unknown), disable tracking and clear known analytics cookies.
    if (window.fbq) window.fbq("consent", "revoke");
    GA_MEASUREMENT_IDS.forEach((id) => {
        window[`ga-disable-${id}`] = true;
    });
    removeKnownAnalyticsCookies();
};

export const subscribeToConsentChanges = (callback) => {
    const handler = (event) => callback(event?.detail?.consent);
    window.addEventListener(CONSENT_EVENT, handler);
    return () => window.removeEventListener(CONSENT_EVENT, handler);
};

const safeNumber = (n) => {
    const x = Number(n);
    return Number.isFinite(x) ? x : 0;
};

// --- Event tracking helpers (Meta Pixel + GA4) ---
export const trackMetaEvent = (eventName, params = {}) => {
    if (!hasAcceptedConsent()) return;
    if (!window.fbq) return;
    window.fbq("track", eventName, params);
};

export const trackGaEvent = (eventName, params = {}) => {
    if (!hasAcceptedConsent()) return;
    if (!window.gtag) return;
    window.gtag("event", eventName, params);
};

export const trackAddToCart = ({ productId, sku, name, category, price, quantity }) => {
    const currency = getCurrency();
    const value = safeNumber(price) * safeNumber(quantity || 1);
    const externalId = productId ? String(productId) : undefined;
    const skuId = sku ? String(sku) : undefined;

    // Meta Pixel (standard event)
    trackMetaEvent("AddToCart", {
        content_ids: externalId ? [externalId] : undefined,
        content_name: name,
        content_category: category,
        content_type: "product",
        value,
        currency,
        ...(skuId ? { content_sku: skuId } : {}),
        ...(skuId ? { content_variant: skuId } : {}),
    });

    // GA4 (recommended event)
    trackGaEvent("add_to_cart", {
        currency,
        value,
        items: [
            {
                item_id: externalId,
                item_name: name,
                item_category: category,
                price: safeNumber(price),
                quantity: safeNumber(quantity || 1),
                ...(skuId ? { item_variant: skuId } : {}),
            },
        ],
    });
};

export const trackInitiateCheckout = ({ value, items = [] }) => {
    const currency = getCurrency();
    const normalizedItems = Array.isArray(items) ? items : [];

    trackMetaEvent("InitiateCheckout", {
        value: safeNumber(value),
        currency,
        num_items: normalizedItems.reduce((acc, it) => acc + safeNumber(it.quantity || 1), 0),
        content_ids: normalizedItems
            .map((it) => it?.externalId || it?._id)
            .filter(Boolean)
            .map(String),
        content_type: "product",
    });

    trackGaEvent("begin_checkout", {
        currency,
        value: safeNumber(value),
        items: normalizedItems.map((it) => ({
            item_id: String(it?.externalId || it?._id || ""),
            item_name: it?.name,
            price: safeNumber(it?.sale_price || it?.regular_price || it?.price),
            quantity: safeNumber(it?.quantity || 1),
            ...(it?.item_code ? { item_variant: String(it.item_code) } : {}),
        })),
    });
};

export const trackPurchase = ({ transactionId, value, items = [] }) => {
    const currency = getCurrency();
    const normalizedItems = Array.isArray(items) ? items : [];

    const contentIds = normalizedItems
        .map((it) => it?.externalId || it?._id)
        .filter(Boolean)
        .map(String);

    trackMetaEvent("Purchase", {
        value: safeNumber(value),
        currency,
        content_ids: contentIds,
        content_type: "product",
        num_items: normalizedItems.reduce(
            (acc, it) => acc + safeNumber(it.quantity || 1), 0
        ),
        contents: normalizedItems
            .map((it) => {
                const id = it?.externalId || it?._id;
                if (!id) return null;
                return {
                    id: String(id),
                    quantity: safeNumber(it?.quantity || 1),
                    item_price: safeNumber(it?.sale_price || it?.regular_price || it?.price),
                };
            })
            .filter(Boolean),
    });

    trackGaEvent("purchase", {
        transaction_id: transactionId ? String(transactionId) : undefined,
        currency,
        value: safeNumber(value),
        items: normalizedItems.map((it) => ({
            item_id: String(it?.externalId || it?._id || ""),
            item_name: it?.name,
            price: safeNumber(it?.sale_price || it?.regular_price || it?.price),
            quantity: safeNumber(it?.quantity || 1),
            ...(it?.item_code ? { item_variant: String(it.item_code) } : {}),
        })),
    });
};

export const trackViewContent = ({ productId, sku, name, category, price }) => {
    const currency = getCurrency();
    const value = safeNumber(price);
    const externalId = productId ? String(productId) : undefined;
    const skuId = sku ? String(sku) : undefined;

    // Meta Pixel (standard event) 
    trackMetaEvent("ViewContent", {
        content_ids: externalId ? [externalId] : undefined,
        content_name: name,
        content_category: category,
        content_type: "product",
        value,
        currency,
        ...(skuId ? { content_sku: skuId } : {}),
        ...(skuId ? { content_variant: skuId } : {}),
    })

    // GA4 (recommended event)
    trackGaEvent("view_item", {
        currency,
        value,
        items: [
            {
                item_id: externalId,
                item_name: name,
                item_category: category,
                price: value,
                quantity: 1,
                ...(skuId ? { item_variant: skuId } : {}),
            },
        ],
    });
};
